import { Link } from "react-router-dom";
import { Youtube, Twitter, Instagram, ShieldAlert } from "lucide-react";
import { Logo } from "../Logo";
import { LEGAL_LINKS, LEGAL_DISCLAIMER_SHORT } from "../../data/legal";
import { SPORTS } from "../../data/sports";

export const Footer = () => {
  return (
    <footer data-testid="footer" className="relative border-t border-brand-navy bg-brand-bg">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          {/* Brand */}
          <div className="md:col-span-4">
            <Logo variant="full" />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-brand-silver">
              Independent AI sports intelligence. Free football & 2026 World Cup
              analysis — built for fans, creators and analysts.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[
                { icon: Youtube, label: "YouTube", id: "footer-social-youtube" },
                { icon: Twitter, label: "X", id: "footer-social-x" },
                { icon: Instagram, label: "Instagram", id: "footer-social-instagram" },
              ].map((s) => (
                <a
                  key={s.id}
                  href="#"
                  data-testid={s.id}
                  aria-label={s.label}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-brand-navy text-brand-silver transition-all duration-300 hover:border-brand-cyan/60 hover:text-brand-cyan"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Sports */}
          <div className="md:col-span-3">
            <h4 className="font-mono-data text-[10px] font-semibold uppercase tracking-[0.25em] text-brand-cyan">
              Sports
            </h4>
            <ul className="mt-4 space-y-2.5">
              {SPORTS.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/sports/${s.slug}`}
                    data-testid={`footer-sport-${s.slug}`}
                    className="flex items-center gap-2 text-sm text-brand-silver transition-colors duration-300 hover:text-white"
                  >
                    {s.name}
                    {s.status !== "active" && (
                      <span className="font-mono-data text-[9px] uppercase tracking-wider text-brand-silver/50">
                        Soon
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Platform */}
          <div className="md:col-span-2">
            <h4 className="font-mono-data text-[10px] font-semibold uppercase tracking-[0.25em] text-brand-cyan">
              Platform
            </h4>
            <ul className="mt-4 space-y-2.5">
              {[
                { label: "Home", to: "/" },
                { label: "Football Hub", to: "/sports/football" },
                { label: "World Cup 2026", to: "/sports/football" },
              ].map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="text-sm text-brand-silver transition-colors duration-300 hover:text-white"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="md:col-span-3">
            <h4 className="font-mono-data text-[10px] font-semibold uppercase tracking-[0.25em] text-brand-cyan">
              Legal
            </h4>
            <ul className="mt-4 space-y-2.5">
              {LEGAL_LINKS.map((l) => (
                <li key={l.slug}>
                  <Link
                    to={`/legal/${l.slug}`}
                    data-testid={`footer-legal-${l.slug}`}
                    className="text-sm text-brand-silver transition-colors duration-300 hover:text-white"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Independent disclaimer */}
        <div
          data-testid="footer-disclaimer"
          className="mt-14 flex items-start gap-3 rounded-xl border-y border-brand-navy bg-brand-navy/20 px-5 py-5"
        >
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-brand-silver" />
          <p className="text-xs leading-relaxed text-brand-silver">{LEGAL_DISCLAIMER_SHORT}</p>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <p className="font-mono-data text-xs text-brand-silver/70">
            © {new Date().getFullYear()} AI First Sports. All rights reserved.
          </p>
          <p className="font-mono-data text-xs text-brand-silver/70">
            Informational & entertainment use only · No betting advice
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
